import React, { useRef, useState } from 'react';
import { t } from 'i18next';
import Swal from 'sweetalert2';

import { Box, TextField } from '@mui/material';
import { SCUIButton } from '../../../atomic/atoms';

import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import MoveUpIcon from '@mui/icons-material/MoveUp';

import { PaymentConsolidationAccountingStatus } from '../enum/SummaryEnums';
import { SummaryCommand } from '../../../services/command';

const ReceiptItem = ({
    item,
    level,
    onChange,
    index,
    isEditing,
    value,
    getBackgroundColor,
    valuesRef,
}: {
    item: any;
    level: number;
    onChange: (id: number | null, value: number) => void;
    index: number;
    background: string;
    isEditing: boolean;
    value?: number;
    getBackgroundColor?: any;
    valuesRef: any;
}) => {
    const fontWeight = 800 - level * 100 > 400 ? 700 - level * 100 : 400;
    const fontSize = `${level < 3 ? 18 - level * 2 : 14}px`;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                paddingY: 0.5,
                paddingX: 1.5,
                width: '100%',
                backgroundColor:
                    item.isOverridenByUser === true ||
                    valuesRef?.current[item.paymentConsolidationItemDefinitionId] !== undefined
                        ? '#c8f7c5'
                        : getBackgroundColor(level, item.children.length > 0, index),
                '&:hover': {
                    backgroundColor:
                        item.isOverridenByUser === true ||
                        valuesRef?.current[item.paymentConsolidationItemDefinitionId] !== undefined
                            ? '#a8e6a2'
                            : '#a0a0a0',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, paddingLeft: level * 4 }}>
                <Box>
                    <p style={{ fontWeight, fontSize }}>{item.description}</p>
                </Box>
            </Box>
            <Box sx={{ minWidth: 120, textAlign: 'right' }}>
                {isEditing && !item.readOnly ? (
                    <TextField
                        size="small"
                        defaultValue={item.amount}
                        variant="outlined"
                        sx={{
                            width: 300,
                            '& input': {
                                fontSize,
                                paddingY: 0.5,
                            },
                        }}
                        onChange={(e) => {
                            if (e.target.value === '0') {
                                Swal.fire({
                                    icon: 'warning',
                                    title: t('general.warning'),
                                    text: t('summary.amountIsZero'),
                                    showConfirmButton: false,
                                    timer: 1500,
                                });
                                return;
                            }
                            const raw = e.target.value;
                            let normalized = raw;

                            if (raw.includes(',') && raw.includes('.')) {
                                if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
                                    // European: 1.234.567,89
                                    normalized = raw.replace(/\./g, '').replace(',', '.');
                                } else {
                                    // US: 12,111.15
                                    normalized = raw.replace(/,/g, '');
                                }
                            } else if (raw.includes(',')) {
                                // Only ',' present: treat as decimal
                                normalized = raw.replace(/\./g, '').replace(',', '.');
                            } else if (raw.includes('.')) {
                                // Only '.' present: treat as thousand separator, remove all
                                normalized = raw.replace(/\./g, '');
                            } else {
                                // Plain number
                                normalized = raw;
                            }

                            const newValue = parseFloat(normalized) || 0;
                            onChange(item.paymentConsolidationItemId, newValue);
                        }}
                    />
                ) : (
                    <p style={{ fontSize }}>
                        {(() => {
                            const displayValue = value ?? item.amount;
                            return displayValue.toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            });
                        })()}
                    </p>
                )}
            </Box>
        </Box>
    );
};

const RecursiveReceipt = ({
    items,
    level = 0,
    onChange,
    isEditing,
    valuesRef,
}: {
    items: any[];
    level?: number;
    onChange: (id: number | null, value: number) => void;
    isEditing: boolean;
    valuesRef: React.MutableRefObject<{ [id: number]: number }>;
}) => {
    const getBackgroundColor = (level: number) => {
        return level === 0 ? '#bbb' : 'transparent';
    };

    return (
        <>
            {(items || []).map((item, index) => {
                const currentIndex = index + 1;
                const background = getBackgroundColor(level);
                const storedValue = valuesRef.current[item.paymentConsolidationItemId];

                return (
                    <React.Fragment key={`${item.name}-${level}-${index}`}>
                        <Box sx={{ width: '100%', borderRadius: '8px' }}>
                            <ReceiptItem
                                item={item}
                                level={level}
                                getBackgroundColor={getBackgroundColor}
                                onChange={onChange}
                                index={currentIndex}
                                background={background}
                                isEditing={isEditing}
                                valuesRef={valuesRef}
                                value={storedValue}
                            />
                        </Box>
                        {item.children?.length > 0 && (
                            <>
                                <RecursiveReceipt
                                    items={item.children}
                                    level={level + 1}
                                    onChange={onChange}
                                    isEditing={isEditing}
                                    valuesRef={valuesRef}
                                />
                            </>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
};

const collectDataRecursive = (items: any[], amounts: any, summaryInfo: any) => {
    const result: any[] = [];

    items.forEach((item) => {
        if (
            item.paymentConsolidationItemId in amounts &&
            amounts[item.paymentConsolidationItemId] !== '' &&
            amounts[item.paymentConsolidationItemId] !== 0
        ) {
            result.push({
                paymentConsolidationItemId: item.paymentConsolidationItemId,
                paymentConsolidationId: summaryInfo.paymentConsolidationId,
                amount: amounts[item.paymentConsolidationItemId],
                isOverridenByUser: true,
                computedAmount: amounts[item.paymentConsolidationItemId],
                paymentConsolidationItemDefinitionId: item.paymentConsolidationItemDefinitionId,
            });
        } else if (item.children && item.children.length > 0) {
            result.push(...collectDataRecursive(item.children, amounts, summaryInfo));
        }
    });

    return result;
};

export const ReceiptRenderer = ({ data, summaryInfo, setIsLoading, setSummary, getSummaryDetail }: any) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const amountsRef = useRef<{ [id: number]: number }>({});

    const handleChange = (id: number | null, value: number) => {
        if (id != null) {
            amountsRef.current[id] = value;
        }
    };

    const handleSubmit = () => {
        const collectedData = collectDataRecursive(data, amountsRef.current, summaryInfo);

        const params = {
            paymentConsolidation: {
                paymentConsolidationId: summaryInfo.paymentConsolidationId,
                PeriodEndDate: summaryInfo.periodEndDate,
                currentVersionId: summaryInfo.currentVersionId,
                paymentConsolidationItemList: collectedData,
            },
        };

        SummaryCommand.UpdateSummary(params)
            .then((res: any) => {
                if (res) {
                    setIsEditing(false);
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    amountsRef.current = {};
                    getSummaryDetail();
                } else {
                    Swal.fire(t('general.anErrorOccured', '', 'error'));
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
            });
    };

    const handleStatus = () => {
        if (summaryInfo.accountStatus === PaymentConsolidationAccountingStatus.PendingAccounting) {
            handlePrint();
            return;
        }
        if (summaryInfo.accountStatus === PaymentConsolidationAccountingStatus.AccountingComplate) {
            handleAxapta();
            return;
        }
    };

    const toggleEditing = () => {
        if (isEditing) {
            handleSubmit();
            return;
        }
        setIsEditing(true);
    };

    const handlePrint = () => {
        setIsLoading(true);
        SummaryCommand.PrintSummary({
            paymentConsolidationId: summaryInfo?.paymentConsolidationId,
            accountingStatus: PaymentConsolidationAccountingStatus.AccountingComplate,
        })
            .then((res: any) => {
                if (res) {
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });

                    setSummary((prev: any) => {
                        return {
                            ...prev,
                            paymentConsolidationId: PaymentConsolidationAccountingStatus.AccountingComplate,
                        };
                    });
                }
                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
                setIsLoading(false);
            });
    };

    const handleAxapta = () => {
        setIsLoading(true);
        SummaryCommand.AxaptaTransferSummary({
            paymentConsolidationId: summaryInfo?.paymentConsolidationId,
        })
            .then((res: any) => {
                if (res) {
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    setSummary((prev: any) => {
                        return {
                            ...prev,
                            paymentConsolidationId: PaymentConsolidationAccountingStatus.TransferToAxaptaComplete,
                        };
                    });
                }
                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
                setIsLoading(false);
            });
    };

    return (
        <Box sx={{ p: 2, width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
                <SCUIButton
                    onClick={toggleEditing}
                    type="primary"
                    sizeType="fixed"
                    size="m"
                    text={isEditing ? t('general.save') : t('general.edit')}
                >
                    {isEditing ? (
                        <SaveIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                    ) : (
                        <EditIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                    )}
                </SCUIButton>
                {[
                    PaymentConsolidationAccountingStatus.AccountingComplate,
                    PaymentConsolidationAccountingStatus.PendingAccounting,
                ].includes(summaryInfo.accountingStatus) && (
                    <SCUIButton
                        onClick={handleStatus}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        text={
                            summaryInfo.accountingStatus === PaymentConsolidationAccountingStatus.AccountingComplate
                                ? t('summary.transferToAxapta')
                                : summaryInfo.accountingStatus ===
                                  PaymentConsolidationAccountingStatus.PendingAccounting
                                ? t('general.print')
                                : ''
                        }
                    >
                        {summaryInfo.accountingStatus === PaymentConsolidationAccountingStatus.AccountingComplate ? (
                            <MoveUpIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                        ) : summaryInfo.accountingStatus === PaymentConsolidationAccountingStatus.PendingAccounting ? (
                            <PrintIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                        ) : (
                            <></>
                        )}
                    </SCUIButton>
                )}
            </Box>
            <RecursiveReceipt items={data} onChange={handleChange} isEditing={isEditing} valuesRef={amountsRef} />
        </Box>
    );
};
